// src/pos-app/surfaces/cashier/TopBarExtras.js
//
// F12a-N Task 7 — the shell top-bar extras for the native cashier surface
// (design contract: docs/mockups/f12a-n/cashier-sell.html, `.topbar` right
// cluster + `#moreMenu`): session pill (green dot · CS-001 · date), Online/
// reconnecting/offline chip (useConnection), and the "…" More menu (Staff
// Allowance / Kitchen Printing toggle / Receipt Translations toggle / End
// Session).
//
// Task N (user-QC finding 3) — the top-bar Orders button (badge + slide-in
// panel trigger) is REMOVED: it duplicated the bottom Orders tab (badge
// count showed "1" while the tab still rendered Phase-7's static empty
// state). There is now exactly ONE Orders surface — the bottom tab, embedded
// full-pane in CashierStation/SellSurface — and its own badge (BottomTabs'
// additive `badge` prop, driven by the same `ordersCount` this file still
// publishes/reads off the bridge) replaces this button. See
// CashierStation.js's `OrdersTabBadge` and SellSurface.js's header note.
//
// Task N (user-QC finding 1) — the session pill NO LONGER leads with the
// cashier name (this reverts Task 8 item 5): the operator chip in
// StationTopBar's right cluster already shows the same name, so the pill
// was showing "Jane Doe" twice on screen — confirmed by the iPad-portrait
// screenshot. The pill is back to `session_id · date` only; this
// supersedes the mockup's `name · CS-id · date` label, which never
// accounted for the shell's operator chip existing at all.
//
// Deliberate omission vs the mockup: NO "Table Service" item. The mockup
// carried it over from the legacy page's navigate('/functions/table-service')
// escape, but in the native app the waiter surface is a SEPARATE STATION
// (per plan Task 7: "switches station? NO — omit nav, note for waiter
// surface") — flagged for the Task 8 parity walk.
//
// Task 8 item 10 — Deliberate omission, DOCUMENTED (not fixed): legacy's
// More-menu-on-mobile-only "sale info" block (CashierSalesPage.js — a
// name/session/date read-out shown INSIDE the dropdown below 768px, because
// the page's top bar itself collapses/hides that info on small screens) has
// no native equivalent. This surface's session pill above is ALWAYS
// rendered in the top bar regardless of viewport (no mobile-specific
// top-bar collapse exists here yet) — reproducing the block would duplicate
// the same three facts a second time. This is intentionally deferred, not
// silently dropped: the native app is tablet-first (spec's HARD no-scroll
// layout assumes a fixed-size viewport); a real phone/narrow-viewport layout
// is out of scope for F12a-N and is its own future surface pass. See
// task-n8-report.md for the parity-walk note.
//
// ── Wiring (the "cleanest wiring" decision the brief asked for) ────────────
// The extras render inside StationTopBar's children slot, which CashierStation
// owns — but every value they show (session id, pending-orders count, toggle
// state) and every action they trigger (open orders panel, staff allowance,
// end session) lives in SellSurface, which owns the single instances of
// useSessionManager / usePendingOrders / the printing prefs. Chosen approach:
// a tiny publish/subscribe CONTEXT BRIDGE defined in this file —
//
//   <SellExtrasProvider>            mounted once by CashierStation, wrapping
//                                   both the top bar and the Sell pane
//   usePublishSellExtras(snapshot)  called by SellSurface: pushes a snapshot
//                                   of primitives + ONE stable ref of action
//                                   callbacks (`api`) into the bridge
//   <TopBarExtras/>                 mounted by CashierStation as StationTopBar
//                                   children; consumes the bridge
//
// Rejected alternatives: lifting the session/pending-orders state to
// CashierStation would either move half of SellSurface's composition up a
// level or force DUPLICATE hook instances (a second usePendingOrders poll —
// explicitly forbidden by the brief); a render-prop from SellSurface can't
// reach the top bar because SellSurface renders inside the Sell pane, a
// sibling subtree of StationTopBar. The bridge keeps single hook instances,
// zero extra polling, and is loop-safe by construction: the publish effect
// depends only on primitives (re-published just when a value changes) plus
// the `api` ref whose identity never changes.
//
// Reused flows stay where their state lives: the menu items only call back
// into SellSurface, which mounts the REUSED StaffAllowanceManager
// (hideButton + externalOpen pulse — legacy CashierSalesPage.js:2264-2276 /
// 2347) and the REUSED EndOfDayFlow (legacy 1809-1819). This component is
// pure chrome.
//
// The More menu is a fixed OVERLAY (scrim + dropdown, mockup `.menu`) — it
// never adds page scroll (spec §6). Escape and scrim-tap both dismiss it;
// Escape is routed through SellSurface's Escape priority chain via the
// SellExtrasContext bridge (`useSellExtrasMenu`) rather than a listener
// local to this file — see the bridge section below.
// Tokens/CSS vars only; logical properties keep it RTL-correct; every
// visual control has a ≥44px hit area (::after expansion on the 38px chrome
// buttons, min-height:44px menu items).
//
// Task N round 3 (user-QC finding 2) — the scrim + dropdown are rendered
// through a React portal to document.body instead of inline where
// TopBarExtras mounts (StationTopBar's `children` slot). Root cause: this
// component renders inside `<header style={glass()}>` (StationTopBar.js),
// and `glass()` sets `backdropFilter` on that header — per the CSS spec,
// any element with a non-none `backdrop-filter` (i) creates a new stacking
// context AND (ii) becomes the containing block for `position:fixed`
// descendants (same trap `filter`/`transform`/`will-change` set). Both
// consequences bit here: `.tbx-menu`'s `position:fixed` was never truly
// relative to the viewport, and — more importantly — being confined to the
// header's stacking context meant its `z-index:60` only ever competed
// against other content INSIDE that header. CashierStation's cart column
// (`.crt-root`, its own backdrop-filter/stacking context) lives in `<main
// className="cst-body">`, a LATER sibling of the header in the DOM; two
// sibling stacking contexts with `z-index:auto` paint in DOM order, so the
// later one (the cart, inside main) always painted on top of the entire
// header layer — menu included — no matter how high z-index:60 was set.
// Portaling to `document.body` (the one node with no filter/backdrop-filter
// ancestor) frees the menu from the header's containing block AND stacking
// context, so it now competes at the true document root, above everything
// else in the app. The `<style>` tag's rules are global regardless of where
// this component mounts, so no CSS changes were needed.
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLanguage } from '../../../context/LanguageContext';
import { useConnection } from '../../ConnectionStatus';

// ---------------------------------------------------------------------
// Context bridge
const SellExtrasContext = createContext(null);

export function SellExtrasProvider({ children }) {
    const [extras, setExtras] = useState(null);
    // The More menu's open/closed state also lives here (not as local state
    // in TopBarExtras) so SellSurface's single Escape priority chain can see
    // and close it — see useSellExtrasMenu below and SellSurface.js's "Escape
    // priority chain" comment. Two independent Escape listeners racing to
    // close the same menu used to violate that chain's one-layer-per-press
    // invariant.
    const [menuOpen, setMenuOpen] = useState(false);
    // setExtras/setMenuOpen from useState are identity-stable, so `value`
    // only changes when a new snapshot is published or the menu toggles.
    const value = useMemo(() => ({ extras, setExtras, menuOpen, setMenuOpen }), [extras, menuOpen]);
    return <SellExtrasContext.Provider value={value}>{children}</SellExtrasContext.Provider>;
}

// Stable no-op fallback for when useSellExtrasMenu is used outside a
// SellExtrasProvider — keeps the returned setter identity stable so it never
// forces the caller's effect to re-attach.
const NOOP_SET_MENU_OPEN = () => {};

// Called by SellSurface's Escape priority chain: the More menu is chrome
// state owned by this bridge (see SellExtrasProvider above), not by
// TopBarExtras itself, so the chain can rank it and close it like any other
// overlay. `setMenuOpen` is the useState setter — identity-stable — so it is
// safe to use directly as an effect dependency.
export function useSellExtrasMenu() {
    const ctx = useContext(SellExtrasContext);
    return {
        menuOpen: ctx ? ctx.menuOpen : false,
        setMenuOpen: ctx ? ctx.setMenuOpen : NOOP_SET_MENU_OPEN,
    };
}

// Task N (finding 3) — CashierStation's `OrdersTabBadge` reads the same
// snapshot TopBarExtras consumes (ordersCount + ready) to drive the bottom
// Orders tab's badge count. CashierStation itself can't call `useContext`
// on the Provider it renders (a component isn't a descendant of the context
// it creates), so this hook — mounted from a small child component inside
// the Provider, same pattern as TopBarExtras and MenuBridgeConsumer-style
// bridge consumers — is the read side for that one extra subscriber.
export function useSellExtrasSnapshot() {
    const ctx = useContext(SellExtrasContext);
    return ctx ? ctx.extras : null;
}

// Called by SellSurface every render with its live snapshot. `api` MUST be a
// stable ref object ({ current: {...callbacks} }) — the effect deps are the
// primitives + that stable identity, so publishing can never loop.
export function usePublishSellExtras({
    ready,
    sessionId,
    sessionDate,
    cashierName,
    ordersCount,
    printingEnabled,
    receiptTranslationsEnabled,
    canTranslateReceipts,
    receiptTranslationLanguage,
    configLoaded,
    api,
}) {
    const ctx = useContext(SellExtrasContext);
    const setExtras = ctx ? ctx.setExtras : null;

    useEffect(() => {
        if (!setExtras) return;
        setExtras({
            ready,
            sessionId,
            sessionDate,
            cashierName,
            ordersCount,
            printingEnabled,
            receiptTranslationsEnabled,
            canTranslateReceipts,
            receiptTranslationLanguage,
            configLoaded,
            api,
        });
        // Both new values are primitives, so they belong in the dep list on
        // the same terms as the rest — the stable-`api`-ref rule in this
        // hook's header comment is unaffected.
    }, [setExtras, ready, sessionId, sessionDate, cashierName, ordersCount,
        printingEnabled, receiptTranslationsEnabled, canTranslateReceipts,
        receiptTranslationLanguage, configLoaded, api]);

    // Clear the bridge when the surface unmounts (e.g. flag flipped back to
    // 'legacy') so the top bar drops back to plain chrome.
    useEffect(() => {
        if (!setExtras) return undefined;
        return () => setExtras(null);
    }, [setExtras]);
}

// ---------------------------------------------------------------------
const CSS = `
.tbx-spacer{flex:1;min-width:0;}
.tbx-chip{display:inline-flex;align-items:center;gap:7px;flex:none;height:32px;padding:0 11px;
  border-radius:999px;font-size:12px;font-weight:700;white-space:nowrap;
  background:var(--c-surface);border:1px solid var(--c-border);color:var(--c-slate);}
/* session pill (Task N finding 1): min-width:0 lets the PILL shrink, but the
   sessionId span inside is flex:none (never truncates) — only the date span
   is allowed to ellipsis, so at tight widths the least-critical part (the
   date) is what gives, never the id the cashier actually needs to read. */
.tbx-chip.session{font-variant-numeric:tabular-nums;min-width:0;flex:0 1 auto;}
.tbx-sid{flex:none;white-space:nowrap;}
.tbx-date{flex-shrink:1;min-width:0;max-width:150px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.tbx-dot{width:7px;height:7px;border-radius:50%;flex:none;}
.tbx-dot.green{background:var(--c-green);box-shadow:0 0 6px color-mix(in srgb, var(--c-green) 80%, transparent);}
.tbx-dot.amber{background:var(--c-amber);box-shadow:0 0 6px color-mix(in srgb, var(--c-amber) 80%, transparent);}
.tbx-dot.red{background:var(--c-red);box-shadow:0 0 6px color-mix(in srgb, var(--c-red) 80%, transparent);}
.tbx-chip.conn-reconnecting{color:var(--c-amber);border-color:var(--c-amberBorder);background:var(--c-amberLight);}
.tbx-chip.conn-offline{color:var(--c-red);border-color:var(--c-redBorder);background:var(--c-redLight);}
.tbx-btn{position:relative;display:inline-flex;align-items:center;justify-content:center;gap:7px;flex:none;
  height:38px;min-width:38px;padding:0 12px;border-radius:11px;cursor:pointer;font-family:inherit;
  background:var(--c-surface);border:1px solid var(--c-border);color:var(--c-slate);
  font-size:13px;font-weight:700;transition:transform .15s ease, background .15s ease;}
.tbx-btn:active{transform:scale(.95);background:var(--c-faint);}
.tbx-btn::after{content:"";position:absolute;inset:-4px;}
.tbx-btn.icon-only{padding:0;width:38px;}

.tbx-scrim{position:fixed;inset:0;z-index:59;background:transparent;}
.tbx-menu{position:fixed;top:calc(64px + var(--safe-top, 0px));inset-inline-end:12px;z-index:60;width:264px;
  background:color-mix(in srgb, var(--c-surface) 92%, transparent);
  border:1px solid var(--c-border);border-radius:14px;padding:6px;box-sizing:border-box;
  -webkit-backdrop-filter:blur(20px) saturate(1.5);backdrop-filter:blur(20px) saturate(1.5);
  box-shadow:0 18px 44px rgba(2,8,23,.22);}
.tbx-mi{display:flex;align-items:center;gap:11px;width:100%;min-height:44px;padding:9px 11px;box-sizing:border-box;
  border:none;border-radius:10px;background:transparent;cursor:pointer;font-family:inherit;
  font-size:13.5px;font-weight:600;color:var(--c-ink);text-align:start;}
.tbx-mi:hover{background:var(--c-greenLight);}
.tbx-mi svg{color:var(--c-muted);flex:none;}
.tbx-mi .lbl{flex:1;min-width:0;}
.tbx-mi.destructive{color:var(--c-red);}
.tbx-mi.destructive svg{color:var(--c-red);}
.tbx-mi.destructive:hover{background:var(--c-redLight);}
.tbx-div{height:1px;background:var(--c-border);margin:5px 8px;}
.tbx-switch{width:38px;height:22px;border-radius:999px;background:var(--c-border);position:relative;flex:none;
  transition:background .15s;}
.tbx-switch::after{content:"";position:absolute;top:2px;inset-inline-start:2px;width:18px;height:18px;border-radius:50%;
  background:#fff;box-shadow:0 1px 3px rgba(0,0,0,.3);transition:inset-inline-start .15s;}
.tbx-switch.on{background:var(--c-green);}
.tbx-switch.on::after{inset-inline-start:18px;}
@media (prefers-reduced-motion: reduce){.tbx-btn,.tbx-switch,.tbx-switch::after{transition:none !important;}}
`;

const MoreIcon = (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
        <circle cx="5" cy="12" r="1.2" fill="currentColor" /><circle cx="12" cy="12" r="1.2" fill="currentColor" /><circle cx="19" cy="12" r="1.2" fill="currentColor" />
    </svg>
);

const AllowanceIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
    </svg>
);

const DrawerIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="9" width="18" height="12" rx="1" /><path d="M3 9l2-5h14l2 5" /><path d="M10 14h4" />
    </svg>
);

const PrinterIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M6 9V3h12v6" /><rect x="4" y="9" width="16" height="8" rx="2" /><path d="M6 15v6h12v-6" />
    </svg>
);

const TranslateIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="m5 8 6 6M4 14l6-6 2-3M2 5h12M7 2h1" /><path d="m22 22-5-10-5 10M14 18h6" />
    </svg>
);

const EndSessionIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M18.36 6.64A9 9 0 1 1 5.64 6.64" /><path d="M12 2v10" />
    </svg>
);

export default function TopBarExtras() {
    const { t } = useLanguage();
    const { online, reconnecting } = useConnection();
    const ctx = useContext(SellExtrasContext);
    const extras = ctx ? ctx.extras : null;
    const menuOpen = ctx ? ctx.menuOpen : false;
    const setMenuOpen = ctx ? ctx.setMenuOpen : NOOP_SET_MENU_OPEN;

    // Escape dismissal for the More menu is NOT handled here — `menuOpen`
    // lives in the SellExtrasContext bridge (see useSellExtrasMenu) so
    // SellSurface's single Escape priority chain (SellSurface.js ~1032,
    // "Escape priority chain") owns it as the topmost-ranked overlay. This
    // used to be a local `useEffect` window keydown listener here, but
    // having two independent Escape listeners meant one press could close
    // this menu AND unwind catalog selection at the same time, violating the
    // chain's "exactly one layer per press" invariant. Click-to-open/close
    // (button + scrim) stays local — only Escape routes through the chain.

    // Nothing published (legacy surface / SellSurface unmounted) or the
    // surface is in a gate state (SoD / EOD / no business day) → plain
    // chrome, mirroring legacy where the header cluster only exists on the
    // main sales render.
    if (!extras || !extras.ready) return null;

    // `cashierName`/`ordersCount` still ride along on the bridge snapshot
    // (SellSurface.js keeps publishing them) — cashierName is no longer
    // rendered here (finding 1: the operator chip already shows it) and
    // ordersCount now feeds CashierStation's `OrdersTabBadge` instead of a
    // button in this component (finding 3).
    const {
        sessionId, sessionDate,
        printingEnabled, receiptTranslationsEnabled,
        canTranslateReceipts, receiptTranslationLanguage,
        configLoaded, api,
    } = extras;

    const call = (name, ...args) => {
        const fn = api && api.current && api.current[name];
        if (typeof fn === 'function') fn(...args);
    };

    const connState = !online ? 'offline' : (reconnecting ? 'reconnecting' : 'online');
    const connDot = connState === 'online' ? 'green' : (connState === 'reconnecting' ? 'amber' : 'red');

    return (
        <>
            <style>{CSS}</style>

            {/* Session pill — green dot · CS-001 · date (mockup .tb-chip).
                Task N finding 1: NO leading cashier name — reverts Task 8
                item 5, which duplicated the operator chip in StationTopBar's
                right cluster (confirmed by the iPad-portrait screenshot
                showing the same name twice). Only the date span is allowed
                to ellipsis (`.tbx-date`) — the session id never truncates. */}
            {sessionId ? (
                <span className="tbx-chip session" data-testid="session-pill">
                    <i className="tbx-dot green" aria-hidden="true" />
                    <span className="tbx-sid">{sessionId}</span>
                    {sessionDate ? <span className="tbx-date">{` · ${sessionDate}`}</span> : null}
                </span>
            ) : null}

            <span className="tbx-spacer" aria-hidden="true" />

            {/* Connection chip (useConnection — same signal KDS's pill shows) */}
            <span className={`tbx-chip conn-${connState}`} role="status" data-testid="conn-chip">
                <i className={`tbx-dot ${connDot}`} aria-hidden="true" />
                {t(connState)}
            </span>

            {/* "…" More menu */}
            <button
                type="button"
                className="tbx-btn icon-only"
                data-testid="more-btn"
                aria-label={t('more')}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen((open) => !open)}
            >
                {MoreIcon}
            </button>

            {/* Task N round 3 finding 2 — portaled to document.body so the
                scrim/menu escape the top bar's backdrop-filter stacking
                context/containing block (see the header comment above the
                `createPortal` import). */}
            {menuOpen && createPortal(
                <>
                    <div className="tbx-scrim" data-testid="more-scrim" onClick={() => setMenuOpen(false)} />
                    <div className="tbx-menu" role="menu" aria-label={t('more')}>
                        <button
                            type="button"
                            className="tbx-mi"
                            role="menuitem"
                            onClick={() => { call('openStaffAllowance'); setMenuOpen(false); }}
                        >
                            {AllowanceIcon}
                            <span className="lbl">{t('staff_allowance')}</span>
                        </button>

                        {/* Task 9 — manual "Open Drawer": opening the till with no
                            sale attached is the classic route for cash to walk, so
                            this only ever calls into the extras api's `openDrawer`,
                            which SellSurface gates behind ManagerGate before any
                            drawer pulse is sent (see SellSurface.js's ManagerGate
                            block). This component never talks to the drawer
                            directly — it is pure chrome, same as every other item
                            here. */}
                        <button
                            type="button"
                            className="tbx-mi"
                            role="menuitem"
                            onClick={() => { call('openDrawer'); setMenuOpen(false); }}
                        >
                            {DrawerIcon}
                            <span className="lbl">{t('open_drawer')}</span>
                        </button>

                        {/* Table Service intentionally omitted — separate waiter
                            station in the native app (see header note). */}

                        {configLoaded && (
                            <>
                                <div className="tbx-div" aria-hidden="true" />
                                {/* Toggles keep the menu open (legacy stopPropagation
                                    semantics on the toggle rows). */}
                                <button
                                    type="button"
                                    className="tbx-mi"
                                    role="menuitemcheckbox"
                                    aria-checked={printingEnabled}
                                    data-testid="printing-toggle"
                                    onClick={() => call('togglePrinting', !printingEnabled)}
                                >
                                    {PrinterIcon}
                                    <span className="lbl">{t('kitchen_bar_printing')}</span>
                                    <span className={`tbx-switch${printingEnabled ? ' on' : ''}`} aria-hidden="true" />
                                </button>
                                {/* Hidden entirely for a single-language tenant.
                                    They have nothing to translate INTO, and
                                    LanguageContext force-disables the setting for
                                    them anyway — so rendering the switch gave a
                                    cashier a control that turned itself back off.
                                    Hidden rather than disabled: a dead switch
                                    invites "why can't I turn this on?", and there
                                    is no answer that helps them. */}
                                {canTranslateReceipts && (
                                    <button
                                        type="button"
                                        className="tbx-mi"
                                        role="menuitemcheckbox"
                                        aria-checked={receiptTranslationsEnabled}
                                        data-testid="translations-toggle"
                                        onClick={() => call('toggleReceiptTranslations', !receiptTranslationsEnabled)}
                                    >
                                        {TranslateIcon}
                                        <span className="lbl">
                                            {t('receipt_translations')}
                                            {/* Naming the language makes the setting
                                                self-explaining — previously nothing on
                                                screen said what "on" would print. */}
                                            {receiptTranslationLanguage
                                                ? ` (${receiptTranslationLanguage.toUpperCase()})`
                                                : ''}
                                        </span>
                                        <span className={`tbx-switch${receiptTranslationsEnabled ? ' on' : ''}`} aria-hidden="true" />
                                    </button>
                                )}
                            </>
                        )}

                        <div className="tbx-div" aria-hidden="true" />
                        <button
                            type="button"
                            className="tbx-mi destructive"
                            role="menuitem"
                            data-testid="end-session"
                            onClick={() => { setMenuOpen(false); call('endSession'); }}
                        >
                            {EndSessionIcon}
                            <span className="lbl">{t('end_session')}</span>
                        </button>
                    </div>
                </>,
                document.body
            )}
        </>
    );
}
